import { useState } from 'react'
import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import { IconAlertTriangle, IconCircleCheck, IconLock, IconPlugConnectedX, IconShieldCheck } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { RestrictedState } from '../access/RestrictedState'
import { useEtlHalts } from '../../hooks/useEtlHalts'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { DETAIL_ADMIN_ONLY, NOT_MEASURED, errorKind, errorSummary, whenLabel } from '../../utils/format'
import { haltDetailView } from '../../utils/haltDetail'
import type { HaltDetailView } from '../../utils/haltDetail'
import { exemptSummary, haltReasonText, haltView } from './requestFormat'
import type { ActiveHaltRow } from './requestFormat'
import { CLEAR_UNAVAILABLE_TEXT, clearHaltGate, haltAuditLink } from './haltClearFormat'
import type { ClearHaltGate } from './haltClearFormat'
import { ClearHaltModal } from './ClearHaltModal'
import type { EtlHalt } from '../../types/etlHalts'
import type { EtlJobCatalog } from '../../types/etlJobs'

/**
 * ETL halts: every blocking halt, what still runs during it, clearing one, and the cleared ones as history.
 *
 * Every blocking halt — not only the newest, which is all the catalogue's `activeHalt` carries — with the
 * categories that still run, and the cleared ones as history: who cleared each (as recorded) and the note
 * they appended.
 *
 * CLEARING is offered per halt, only from the FULL list (never the catalogue's fallback banner), and only when
 * the server says `canClearHalts: true` — the write is pinned to an Entra sign-in holding Lighthouse.Admin, and
 * fails closed on a missing answer. Refused, the button is shown dimmed with the server's reason; unavailable,
 * the panel says how an administrator clears one instead.
 *
 * Loading, failed and "nothing halted" look different. A failed read says outright that it says nothing
 * about whether ETL is halted — and if the catalogue does know of a halt, that banner is still shown.
 *
 * THE DETAIL is administrator-only (D8): for anyone else the server sends `detailWithheld: true`, and the panel says
 * DETAIL_ADMIN_ONLY, dimmed — never "No detail was recorded", which would be false. The reason is always shown.
 */
export function HaltPanel({
  catalog,
  entraConfigured,
}: {
  catalog: EtlJobCatalog | undefined
  /** Whether this build can sign anyone in with Entra — passed in, because src/auth reads window at load. */
  entraConfigured: boolean
}) {
  const { data: halts, isLoading, isError, error } = useEtlHalts(true)
  const view = haltView(halts, isLoading, isError, catalog)
  const gate = clearHaltGate(catalog, entraConfigured)
  const [clearing, setClearing] = useState<EtlHalt | null>(null)

  if (view.kind === 'failed') {
    return (
      <Stack gap="sm">
        {errorKind(error) === 'forbidden' ? (
          <RestrictedState surface="The ETL halt list" compact />
        ) : (
          <Alert
            icon={<IconPlugConnectedX size={16} />}
            color="yellow"
            variant="light"
            title="Could not load halts — this says nothing about whether ETL is halted"
          >
            <Text size="sm">{errorSummary(error) ?? 'The request failed.'}</Text>
          </Alert>
        )}
        {view.fallback.length > 0 && (
          <ActiveHaltAlert rows={view.fallback} count={view.count} catalog={catalog} partial />
        )}
      </Stack>
    )
  }

  if (view.kind === 'loading') {
    // The catalogue's banner while the full list loads: a known halt is never hidden behind a spinner.
    return view.fallback.length > 0 ? (
      <ActiveHaltAlert rows={view.fallback} count={view.count} catalog={catalog} partial />
    ) : (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Checking for ETL halts…
        </Text>
      </Group>
    )
  }

  return (
    <Stack gap="sm">
      {view.kind === 'halted' ? (
        <>
          <ActiveHaltAlert
            rows={view.active}
            count={view.active.length}
            catalog={catalog}
            clear={{ gate, onClear: (row) => setClearing(view.active.find((h) => h.runId === row.runId) ?? null) }}
          />
          <ClearHaltModal
            key={clearing?.runId ?? 'closed'}
            halt={clearing}
            active={view.active}
            entraConfigured={entraConfigured}
            onClose={() => setClearing(null)}
          />
        </>
      ) : (
        <Group gap={6}>
          <IconCircleCheck size={14} style={{ color: 'var(--color-text-disabled)' }} />
          <Text size="xs" c="dimmed">
            Not halted: no unacknowledged ETL halt is recorded.
          </Text>
        </Group>
      )}
      {view.cleared.length > 0 && <HaltHistory halts={view.cleared} />}
    </Stack>
  )
}

function ActiveHaltAlert({
  rows,
  count,
  catalog,
  partial = false,
  clear,
}: {
  rows: ActiveHaltRow[]
  /** How many halts are blocking — may exceed `rows` when only the catalogue's newest is known. */
  count: number
  catalog: EtlJobCatalog | undefined
  /** True when the rows are the catalogue's summary rather than the full list. */
  partial?: boolean
  /** The clear control. Given ONLY for the full list: the catalogue's fallback banner never offers a clear. */
  clear?: { gate: ClearHaltGate; onClear: (row: ActiveHaltRow) => void }
}) {
  const navigate = useNavigate()
  // Only the catalogue says which categories still run; an older API serves no list and exempted nothing.
  const exempt = exemptSummary(catalog)

  return (
    <Alert
      icon={<IconAlertTriangle size={16} />}
      color="red"
      title={`ETL is halted${count > 1 ? ` (${count} halts)` : ''}`}
    >
      <Stack gap="sm">
        {rows.map((row) => {
          const detail = haltDetailView(row.detail, row.detailWithheld)
          return (
            <Stack key={row.runId} gap={2}>
              <Group gap="xs" wrap="wrap">
                <Badge size="sm" color="red" variant="light">
                  {haltReasonText(row.reason)}
                </Badge>
                <Text size="sm">on run</Text>
                <Anchor
                  component="button"
                  type="button"
                  size="sm"
                  ff="monospace"
                  onClick={() => navigate(`/execution-history/${encodeURIComponent(row.runId)}`)}
                >
                  {row.runKey}
                </Anchor>
                <Text size="xs" c="dimmed">
                  {row.orchestratorName ? `${row.orchestratorName} · ` : ''}halted {whenLabel(row.haltedAt)}
                </Text>
              </Group>
              <HaltDiagnosis view={detail} />
              {clear && clear.gate.kind !== 'unavailable' && (
                <Group>
                  <ClearHaltButton gate={clear.gate} onClick={() => clear.onClear(row)} />
                </Group>
              )}
            </Stack>
          )
        })}

        {partial && count > rows.length && (
          <Text size="xs" c="dimmed">
            Only the newest is shown: the full halt list has not loaded.
          </Text>
        )}

        {exempt.kind === 'unknown' ? (
          <Text size="sm">
            Still runs during a halt: not known yet — the job catalogue has not caught up with this halt.
          </Text>
        ) : exempt.kind === 'none' ? (
          <Text size="sm">Still runs during a halt: nothing. Every job is refused until the halt is cleared.</Text>
        ) : (
          <>
            <Text size="sm">Still runs during a halt: {exempt.categories.join(', ')}.</Text>
            {exempt.includesDiagnostics && (
              <Text size="sm">
                The nightly Data Comb runs during a halt anyway, so it can be run on demand too. Maintenance,
                including the retention jobs that free space, stays blocked.
              </Text>
            )}
          </>
        )}

        {!clear ? (
          <Text size="xs" c="dimmed" data-state="clear-not-offered">
            Clearing is offered from the full halt list, which has not loaded.
          </Text>
        ) : clear.gate.kind === 'refused' ? (
          <Text size="xs" c="dimmed" data-state="clear-refused">
            You cannot clear a halt from this session. {clear.gate.reason}
            {clear.gate.remedy && <> {clear.gate.remedy}</>}
          </Text>
        ) : clear.gate.kind === 'unavailable' ? (
          <Text size="xs" c="dimmed" data-state="clear-unavailable">
            {CLEAR_UNAVAILABLE_TEXT}
          </Text>
        ) : null}
      </Stack>
    </Alert>
  )
}

/**
 * The halt's diagnosis, or why it is not shown: withheld from this caller (dimmed, DETAIL_ADMIN_ONLY), or none
 * recorded. ClearHaltModal shows the same line.
 */
function HaltDiagnosis({ view }: { view: HaltDetailView }) {
  if (view.kind === 'withheld') {
    return (
      <Text size="sm" c="dimmed" data-state="detail-withheld">
        {DETAIL_ADMIN_ONLY}
      </Text>
    )
  }
  return <Text size="sm">{(view.kind === 'recorded' ? view.diagnosis : null) ?? 'No detail was recorded.'}</Text>
}

/** A withheld cell in the history table: dimmed, never blank and never the unmeasured dash. */
function WithheldCell() {
  return (
    <Text size="xs" c="dimmed" data-state="detail-withheld">
      {DETAIL_ADMIN_ONLY}
    </Text>
  )
}

/**
 * One halt's "Clear this halt…" button. Refused, it is shown — dimmed, not hidden — with the server's reason on
 * hover (and inline below the halts). `data-disabled` rather than `disabled`, so the tooltip still receives the
 * pointer; the click is refused in the handler instead.
 */
function ClearHaltButton({ gate, onClick }: { gate: ClearHaltGate; onClick: () => void }) {
  const refused = gate.kind === 'refused'
  const reason = refused ? [gate.reason, gate.remedy].filter(Boolean).join(' ') : null

  return (
    <Tooltip label={reason} disabled={!refused} multiline w={320} withArrow>
      <Button
        size="xs"
        variant="light"
        color="red"
        leftSection={refused ? <IconLock size={14} /> : <IconShieldCheck size={14} />}
        data-disabled={refused || undefined}
        aria-disabled={refused || undefined}
        data-action="clear-halt"
        onClick={(event) => {
          if (refused) {
            event.preventDefault()
            return
          }
          onClick()
        }}
      >
        Clear this halt…
      </Button>
    </Tooltip>
  )
}

function HaltHistory({ halts }: { halts: EtlHalt[] }) {
  const navigate = useNavigate()
  // The audit trail is a Lighthouse.Admin page: link to it only when the server says this caller holds the role.
  const admin = useLighthouseAdmin()

  return (
    <Accordion variant="contained" chevronPosition="left">
      <Accordion.Item value="history">
        <Accordion.Control>
          <Group gap="xs">
            <Text size="sm">Halt history</Text>
            <Badge size="sm" variant="default">
              {halts.length} cleared
            </Badge>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Run</Table.Th>
                <Table.Th>Reason</Table.Th>
                <Table.Th>Halted</Table.Th>
                <Table.Th>Cleared</Table.Th>
                <Table.Th>Cleared by</Table.Th>
                <Table.Th>Note</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {halts.map((halt) => {
                const detail = haltDetailView(halt.detail, halt.detailWithheld)
                return (
                  <Table.Tr key={halt.runId}>
                    <Table.Td>
                      <Anchor
                        component="button"
                        type="button"
                        size="xs"
                        ff="monospace"
                        onClick={() => navigate(`/execution-history/${encodeURIComponent(halt.runId)}`)}
                      >
                        {halt.runKey}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{haltReasonText(halt.reason)}</Text>
                      {detail.kind === 'withheld' ? (
                        <WithheldCell />
                      ) : (
                        detail.kind === 'recorded' && detail.diagnosis && (
                          <Text size="xs" c="dimmed">
                            {detail.diagnosis}
                          </Text>
                        )
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{whenLabel(halt.haltedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{whenLabel(halt.acknowledgedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        {halt.acknowledgedBy ?? NOT_MEASURED}
                      </Text>
                      {admin.state === 'admin' && (
                        <Anchor
                          component="button"
                          type="button"
                          size="xs"
                          data-link="halt-audit"
                          onClick={() => navigate(haltAuditLink(halt.runId))}
                        >
                          Audit trail
                        </Anchor>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {detail.kind === 'withheld' ? (
                        <WithheldCell />
                      ) : (
                        <Text size="xs">{(detail.kind === 'recorded' ? detail.note : null) ?? NOT_MEASURED}</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
          <Text size="xs" c="dimmed" mt="xs">
            &ldquo;Cleared by&rdquo; is the administrator&apos;s directory object id, the identifier the audit
            trail records; older clears may show a token sub or &lsquo;ApiKey&rsquo;. A note too long to sit
            beside the diagnosis reads &ldquo;see audit trail&rdquo;, which holds it in full. A dash under Note
            means none was given; &ldquo;{DETAIL_ADMIN_ONLY}&rdquo; means a detail was recorded and only an
            administrator may read it.
          </Text>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}
